import EditableDropdown from '../../../../common/EditableDropdown/EditableDropdown';
import styles from './AuditedFinancialsVisualisation.module.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuditedTableData, fetchMisTableOptions, updateAuditedTableData } from '../../../../store/company/action';
import { useDispatch } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { getTimeStamp } from '../../../../utils/dateUtils';
import ClassifiedTable from '../../components/Tables/ClassifiedTable/ClassifiedTable';
import expandMore from '../../../../assets/expand-more.svg';
import RawTable from '../../components/Tables/RawTable/RawTable';
import { toast } from 'react-toastify';
import { isEmpty, removeKeyAndConvertIdToNumber } from '../../../../utils/utils';
import { DATA_FETCH_STATUS } from '../../../../store/dashboard/constants';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../utils/constants/commonConstants';
import { noDataFoundEmptyState } from '../../../../assets/hostedassets';
import FinancialLoaderComponent from '../../../../common/FinancialLoader/FinancialLoaderComponent';
import A4Page from '../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../store/pdfReport/pdfReport_constants';
import { useMcaPolling } from '../../../../utils/customHooks';
import AskAicaQuestionBadge from '../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import AuditorCommentsPill from '../../../../common/AuditorCommentsPill/AuditorCommentsPill';
import clsx from 'clsx';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { ctaPurpleRight } from '../../../../assets/hostedassets';
import { useAuditedType, toNature } from './useAuditedType';
import AuditedFinancials from '../AuditedFinancials/AuditedFinancials';

const AUDITED_TABLE_INITIAL_SCROLL: 'left' | 'right' = 'right';

const AuditedFinancialsVisualisation = ({ selectedMetric = '', isPDFReport = false, assesseeOrgId = '', companyName = '' }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [tableType, setTableType] = useState<string>('');
    const queryParams = useParams();
    const [tableData, setTableData] = useState<any>([{}]);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [changedData, setChangedData] = useState<any>([]);
    const [metric, setMetric] = useState<string>('');
    const [rawOptions, setRawOptions] = useState<any>([]);
    const [rawTableData, setRawTableData] = useState<any>([{}]);
    const [loading, setLoading] = useState<boolean>(true);
    const [metricOptions, setMetricOptions] = useState<string[]>([]);
    const [metricOptionsObject, setMetricsOptionsObject] = useState([]);
    const [loaderMessage, setLoaderMessage] = useState<string>('');
    const [status, setStatus] = useState<string>('');

    // Whether the user has explicitly picked consolidated/standalone at least once.
    const userHasSelected = useRef(false);
    /** Latest audited type for polling — interval callbacks must not close over stale `auditedType`. */
    const auditedTypeRef = useRef<string | null>(null);

    const pollingIntervalRef: any = useRef(null);

    const orgId = queryParams.id || assesseeOrgId;
    const { mcaDocData } = useMcaPolling(orgId);
    const isMcaCalled = mcaDocData?.isFetching === true && mcaDocData?.reportStatus === DATA_FETCH_STATUS.PROCESSING;

    const hasData = !isEmpty(tableData?.headers);
    const showEmptyState = !isPDFReport && !loading && !isMcaCalled && !hasData;

    // ── Data fetching ──────────────────────────────────────────────────────────

    const fetchTableData = useCallback(
        (currentMetric: string, nature?: string, isPolling = false) => {
            if (!currentMetric) return;
            if (!isPolling) setLoading(true);
            dispatch(
                fetchAuditedTableData(
                    {
                        type: 'auditedfinancials',
                        mis: currentMetric,
                        orgId: orgId || '',
                        requestId: getTimeStamp(),
                        raw: false,
                        nature,
                    },
                    {
                        onError: () => setLoading(false),
                        onSuccess: (data, statusRes) => {
                            setStatus(statusRes?.auditedFinancialsStatus);
                            setLoading(false);
                            setTableData(data);
                        },
                    },
                ),
            );
        },
        [dispatch, orgId],
    );

    // Called by useAuditedType when user changes dropdown
    const onConsolidationChange = useCallback(
        (nature: string) => {
            userHasSelected.current = true;
            setTableType('');
            setIsEditing(false);
            setChangedData([]);
            fetchTableData(metric, nature);
        },
        [metric, fetchTableData],
    );

    const {
        auditedType,
        latestConsolidatedFY,
        latestStandaloneFY,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromYears,
    } = useAuditedType(onConsolidationChange);

    auditedTypeRef.current = auditedType;

    const fetchOptions = () => {
        dispatch(
            fetchMisTableOptions({
                orgId: orgId || '',
                source: 'AicaAuditedFinancials',
                onSuccess: (res) => {
                    const optionsData = res?.data?.data;
                    const misOptionsBlock = optionsData?.[0];
                    setRawOptions(misOptionsBlock?.misOptions);
                    setMetricOptions(misOptionsBlock?.misOptions?.map((item) => item.misDisplayName) ?? []);
                    setMetricsOptionsObject(misOptionsBlock?.misOptions || []);

                    const consolidationBlock = optionsData?.find(
                        (block: any) => block?.latestConsolidatedFY !== undefined || block?.latestStandaloneFY !== undefined,
                    );
                    setAuditedTypeFromYears(
                        consolidationBlock?.latestConsolidatedFY ?? null,
                        consolidationBlock?.latestStandaloneFY ?? null,
                    );
                },
            }),
        );
    };

    const fetchRawData = (row: { [key: string]: string }, header: { [key: string]: string }) => {
        if (header.name === 'Classification' && row?.[header.key] && !row.isAggregated) {
            setLoading(true);
            setTableType(row.classificationName);
            dispatch(
                fetchAuditedTableData(
                    {
                        type: 'auditedfinancials',
                        mis: metric,
                        orgId: orgId || '',
                        requestId: getTimeStamp(),
                        classificationId: row.classificationId,
                        raw: true,
                        nature: toNature(auditedType),
                    },
                    {
                        onError: () => setLoading(false),
                        onSuccess: (data) => {
                            setLoading(false);
                            setRawTableData(data);
                        },
                    },
                ),
            );
        }
    };

    // ── Effects ─────────────────────────────────────────────────────────────────

    useEffect(() => {
        orgId && fetchOptions();
    }, [orgId]);

    useEffect(() => {
        showLoaderMessage(!isEmpty(tableData?.headers));
    }, [loading]);

    useEffect(() => {
        if (metric) {
            // undefined nature until user explicitly picks — backend auto-resolves on initial load
            const nature = userHasSelected.current ? toNature(auditedType) : undefined;
            fetchTableData(metric, nature);
            setIsEditing(false);
            setTableType('');
            setChangedData([]);
        }
    }, [metric]);

    useEffect(() => {
        if (selectedMetric) return;
        if (status === DATA_FETCH_STATUS.PROCESSING) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = setInterval(() => {
                const nature = userHasSelected.current ? toNature(auditedTypeRef.current) : undefined;
                fetchTableData(metric, nature, true);
            }, 5000);
        } else {
            clearInterval(pollingIntervalRef?.current);
        }
        showLoaderMessage(!isEmpty(tableData?.headers));
        return () => {
            clearInterval(pollingIntervalRef?.current);
        };
    }, [status, metric]);

    useEffect(() => {
        if (metricOptions.includes('Other Metric (MIS)')) {
            let temp = metricOptions;
            temp = temp.filter((item) => item !== 'Other Metric (MIS)');
            setMetricOptions(temp);
        }
    }, [metricOptions]);

    useEffect(() => {
        if (metricOptionsObject?.length && metricOptions?.length) {
            if (selectedMetric) setMetric(selectedMetric);
            else {
                let first = (metricOptionsObject as any).find((item: any) => item?.isDataAvailable === true)?.misDisplayName;
                setMetric(first || metricOptions?.[0]);
            }
            //@ts-ignore
        }
    }, [metricOptionsObject, metricOptions, selectedMetric]);

    useEffect(() => {
        const tableHeadersLength = tableData?.headers?.length;
        if (!isPDFReport || !tableHeadersLength) return;

        if (tableHeadersLength > 1) {
            const headersForPDFReport = [
                tableData.headers[0],
                ...(tableHeadersLength > 3 ? [tableData.headers[tableHeadersLength - 3]] : []),
                ...(tableHeadersLength > 2 ? [tableData.headers[tableHeadersLength - 2]] : []),
                tableData.headers[tableHeadersLength - 1],
            ];

            if (JSON.stringify(tableData.headers) !== JSON.stringify(headersForPDFReport)) {
                setTableData((data) => ({
                    ...data,
                    headers: headersForPDFReport,
                }));
            }
        }
    }, [isPDFReport, tableData?.headers]);

    // ── Handlers ────────────────────────────────────────────────────────────────

    const handleSave = () => {
        dispatch(
            updateAuditedTableData({
                orgId: queryParams.id ?? '',
                metrics: metric,
                changedData: removeKeyAndConvertIdToNumber(changedData, 'name'),
                source: 'AicaAuditedFinancials',
                onSuccess: () => {
                    const nature = userHasSelected.current ? toNature(auditedType) : undefined;
                    fetchTableData(metric, nature);
                    setIsEditing(false);
                    setChangedData([]);
                    setTableType('');
                    toast.success('Data saved successfully');
                },
                onError: () => {
                    toast.error('Failed to save data');
                },
            }),
        );
    };

    const showAllFinancialMetrics = () => {
        updateQueryParams(searchParams, setSearchParams, { showFinancialMetrics: true }, location);
    };

    function showLoaderMessage(tableData) {
        if (isPDFReport) return '';
        if (isMcaCalled || loading) setLoaderMessage('Fetching latest data from MCA (takes upto 24 hours)');
        else if (!tableData) setLoaderMessage('No Data Available');
    }

    const handleTabChange = (tab: string) => {
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        setMetric(tab);
    };

    const tabs = metricOptions.map((optionName) => {
        const optionObj = (metricOptionsObject as any[]).find((item) => item.misDisplayName === optionName);
        return {
            label: optionName,
            isAvailable: optionObj?.isDataAvailable ?? true,
        };
    });

    // ── Render ──────────────────────────────────────────────────────────────────

    const render = (
        <div>
            {!isPDFReport && (
                <div className={styles.TitleRow}>
                    <div className={styles.TitleLeft}>
                        <h2 className={styles.PageTitle}>Audited Financials</h2>
                    </div>
                    <div className={styles.TitleRight}>
                        {(isMcaCalled || loading) && (
                            <FinancialLoaderComponent tableName="" message={loaderMessage} status={isMcaCalled || loading} />
                        )}
                        {!selectedMetric && (
                            <>
                                <div className={styles.ViewFinancialRatiosBadge} onClick={() => showAllFinancialMetrics()}>
                                    <img src={ctaPurpleRight} alt={'upload'} />
                                    Financial Ratios
                                </div>
                                <AuditorCommentsPill orgId={orgId} />
                                <AuditedFinancials assesseeOrgId={orgId} companyName={companyName} />
                            </>
                        )}
                    </div>
                </div>
            )}

            {!isPDFReport && hasData && (
                <div className={styles.ControlsRow}>
                    <div className={styles.ControlsLeft}>
                        <div className={styles.ConsolidatedDropdown}>
                            <EditableDropdown
                                id={'ConsolidatedSelector'}
                                value={auditedType ?? ''}
                                options={[AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE]}
                                optionsObject={auditedTypeOptionsObject}
                                setOutput={handleConsolidationChange}
                                removeInput={true}
                                optionStyle={{ fontSize: '14px' }}
                                placeHolder="NA"
                                disabled={latestConsolidatedFY === null && latestStandaloneFY === null}
                            />
                        </div>
                        <div className={styles.TabGroup}>
                            {tabs.map(({ label, isAvailable }) => (
                                <button
                                    key={label}
                                    className={clsx(styles.TabButton, {
                                        [styles.TabButtonActive]: metric === label,
                                        [styles.TabButtonDisabled]: !isAvailable,
                                    })}
                                    onClick={() => isAvailable && handleTabChange(label)}
                                    disabled={!isAvailable}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {!selectedMetric && <div className={styles.ControlsRight}></div>}
                </div>
            )}

            {/* ── PDF Report header ── */}
            {isPDFReport && (
                <div className={styles.HeadingContainer}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <h3>Financial Statements</h3>
                        <div className={styles.divider} />
                        <EditableDropdown
                            id={'MetricSelector'}
                            optionStyle={{ fontSize: '14px' }}
                            value={metric}
                            options={metricOptions}
                            setOutput={(val) => {
                                if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
                                setMetric(val);
                            }}
                            removeInput={true}
                            placeHolder={'Select Metric'}
                            optionsObject={metricOptionsObject}
                        />
                    </div>
                </div>
            )}

            <>
                {showEmptyState && (
                    <div className={styles.EmptyState}>
                        <img src={noDataFoundEmptyState} alt="no data" height={80} width={100} />
                        <div className={styles.EmptyStateText}>No Data Available</div>
                    </div>
                )}

                {tableType && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            margin: '0.5rem 0',
                            padding: '0 0.5rem',
                        }}
                    >
                        <div className={styles.Breadcrumb}>
                            <span
                                className={styles.Blue}
                                onClick={() => {
                                    setTableType('');
                                }}
                            >
                                {metric}
                            </span>
                            <img src={expandMore} alt="exp" height="10px" />
                            {tableType}
                        </div>
                        {isEditing ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontFamily: 'Gilroy-Semibold,serif',
                                    fontSize: '14px',
                                }}
                            >
                                <div>Save Classification</div>
                                <button
                                    onClick={() => {
                                        setIsEditing(!isEditing);
                                        setChangedData([]);
                                    }}
                                    className={styles.CancelClassification}
                                >
                                    Cancel
                                </button>
                                <button onClick={() => handleSave()} className={styles.SaveClassification}>
                                    <img
                                        src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/saveClassificationInvestor.svg'}
                                        alt={'save'}
                                    />
                                    Save
                                </button>
                            </div>
                        ) : (
                            <div onClick={() => setIsEditing(!isEditing)} className={styles.EditClassification}>
                                <img src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/editClassification.svg'} alt="edit" />
                                Edit Classification
                            </div>
                        )}
                    </div>
                )}
                <div
                    className={clsx(styles.TableContainer, {
                        [styles.NoTableData]: !hasData,
                    })}
                >
                    {tableType && (
                        <>
                            <RawTable
                                data={rawTableData}
                                loading={loading}
                                freezeColumnEnd={2}
                                freezeColumnStart={0}
                                isEditing={isEditing}
                                metric={metric}
                                rawOptions={rawOptions}
                                changedData={changedData}
                                setChangedData={setChangedData}
                            />
                        </>
                    )}
                    {tableType === '' && hasData && (
                        <ClassifiedTable
                            data={tableData}
                            loading={false}
                            handleClick={fetchRawData}
                            metric={metric}
                            isPDFReport={isPDFReport}
                            initialHorizontalScroll={AUDITED_TABLE_INITIAL_SCROLL}
                        />
                    )}
                </div>
            </>
        </div>
    );

    if (isPDFReport)
        return tableData?.headers?.length ? (
            <A4Page sectionName={REPORT_SECTION_NAMES.AUDITED_FINANCIALS}>
                <div className={styles.Card}>
                    <div className={styles.Top}>
                        <div className={styles.Title}>
                            Financial Statements&nbsp;
                            <span className={styles.Secondary}>| {metric}</span>
                        </div>
                        <div className={styles.Right}>All values are in INR</div>
                    </div>
                    <div className={styles.Body}>{render}</div>
                </div>
            </A4Page>
        ) : (
            <></>
        );
    else return <>{render}</>;
};

export default AuditedFinancialsVisualisation;
